import {FC} from "react";
import {useTranslation} from "react-i18next";
import startCase from "lodash.startcase";
import {Badge} from "@interstate/components/Badge";
import {
  BuildingOfficeIcon,
  MegaphoneIcon,
  XCircleIcon
} from "@interstate/components/Icons";
import {IconType} from "../../../lib/interstate";
import {BadgeMetadata} from "../../../badges";
import {TabRow} from "../../../tab-content";
import "./ClientHierarchyStatusBar.scss";

export interface ClientHierarchyStatusBarProps {
  id: string;
  status?: string | null;
}

/**
 * Unique Statuses:
 * - IN BUSINESS
 * - OUT OF BUSINESS
 * - PENDING OPENING
 * - CONF PENDING OPENING
 * - CIO IN PROGRESS
 * - CONF CIO IN PROGRESS
 * - UNDEFINED
 */
const statusBadgeMetadata = (status: string): BadgeMetadata<IconType> => {
  switch (status) {
    case "IN BUSINESS":
      return {
        labelKey: "badges.client-status.in-business.label",
        variant: "success",
        startIcon: BuildingOfficeIcon
      };
    case "OUT OF BUSINESS":
      return {
        labelKey: "badges.client-status.out-of-business.label",
        variant: "error",
        startIcon: XCircleIcon
      };
    case "PENDING OPENING":
      return {
        labelKey: "badges.client-status.pending-opening.label",
        variant: "caution",
        startIcon: MegaphoneIcon
      };
    case "CONF PENDING OPENING":
      return {
        labelKey: "badges.client-status.conf-pending-opening.label",
        variant: "caution",
        startIcon: MegaphoneIcon
      };
    case "CIO IN PROGRESS":
      return {
        labelKey: "badges.client-status.cio-in-progress.label",
        variant: "caution",
        startIcon: MegaphoneIcon
      };
    case "CONF CIO IN PROGRESS":
      return {
        labelKey: "badges.client-status.conf-cio-in-progress.label",
        variant: "caution",
        startIcon: MegaphoneIcon
      };
    default:
      // TODO: Client Entity API data exposes a custStatusDesc that we could be using
      return {labelKey: startCase(status.toLowerCase()), variant: "info"};
  }
};

export const ClientHierarchyStatusBar: FC<ClientHierarchyStatusBarProps> = ({
  id,
  status
}) => {
  const {t} = useTranslation();

  if (status && status !== "UNDEFINED") {
    const sanitizedStatus = status
      .toLowerCase()
      .replaceAll(" ", "-")
      .replaceAll("_", "-");
    const statusId = `client-hierarchy-status-${sanitizedStatus}-${id.toLowerCase()}`;
    const {
      labelKey,
      variant,
      startIcon: StartIcon
    } = statusBadgeMetadata(status);

    return (
      <TabRow name={"client-hierarchy-status-bar"}>
        <Badge
          id={`${statusId}-badge`}
          data-testid={statusId}
          className={`client-hierarchy-status-badge client-hierarchy-status-badge--${variant} client-hierarchy-badge-${sanitizedStatus}`}
          variant={variant}
          startIcon={
            StartIcon ? (
              <StartIcon
                id={`${statusId}-icon`}
                data-testid={`${statusId}-icon`}
              />
            ) : undefined
          }>
          <span className={"client-hierarchy-status-badge-label"}>
            {t(labelKey!)}
          </span>
        </Badge>
      </TabRow>
    );
  }

  return null;
};
